import { EMAIL_PATTERN } from "../../common/email";

// The four kinds that are a scheme and a line of escaping after it, which is what puts them in one module: a camera
// reads each of these off to another app on the phone rather than showing anybody the text.

// A URL is what the page emits rather than what was typed: `href` is the reading a scanner will make of it, with the
// spaces percent-encoded and the host lower-cased, and the box under the code shows that reading and not the draft.
export function linkUrl(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    // A bare host is what somebody types and never a URL, so the scheme is added where there is none to read.
    return new URL(SCHEME.test(trimmed) ? trimmed : `https://${trimmed}`).href;
  } catch {
    return null;
  }
}

export function writeLink(text: string): string {
  return linkUrl(text) ?? "";
}

export function linkProblem(text: string): string | null {
  if (!text.trim()) return null;
  return linkUrl(text) === null ? "Enter a web address" : null;
}

export function writeMail(fields: MailFields): string {
  const address = fields.address.trim();
  if (!address) return "";
  const query: string[] = [];
  if (fields.subject) query.push(`subject=${encodeURIComponent(fields.subject)}`);
  if (fields.body) query.push(`body=${encodeURIComponent(fields.body)}`);
  // The address is left as it was typed: percent-encoding an `@` is valid and is a line nobody can read back.
  return query.length > 0 ? `mailto:${address}?${query.join("&")}` : `mailto:${address}`;
}

export interface MailFields {
  address: string;
  subject: string;
  body: string;
}

// What is left of a number once the punctuation somebody wrote it with is taken off, since a `tel:` is dialled and
// not read. The leading plus survives because it is the country code and not decoration.
export function telNumber(text: string): string {
  const trimmed = text.trim();
  const plus = trimmed.startsWith("+") ? "+" : "";
  return plus + trimmed.replace(UNDIALLABLE, "");
}

export function writeCall(text: string): string {
  const number = telNumber(text);
  return number === "" || number === "+" ? "" : `tel:${number}`;
}

// `SMSTO:` rather than RFC 5724's `sms:`, that being the spelling every scanner reads and the one the phones that
// only ever learnt one of the two learnt. The message runs to the end of the line, so a colon in it is safe.
export function writeSms(fields: SmsFields): string {
  const number = telNumber(fields.number);
  if (number === "" || number === "+") return "";
  return fields.message ? `SMSTO:${number}:${fields.message}` : `SMSTO:${number}`;
}

export interface SmsFields {
  number: string;
  message: string;
}

export function addressProblem(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  return EMAIL_PATTERN.test(trimmed) ? null : "Enter a valid address";
}

export function numberProblem(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (WRITTEN.test(trimmed)) return "A number is digits, spaces and + - ( )";
  return /[0-9]/.test(trimmed) ? null : "A number needs at least one digit";
}

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

// Everything a keypad has no key for. The pause and wait characters stay, an extension being part of the number.
const UNDIALLABLE = /[^0-9*#,;]/g;

// How a number is written down, against how it is dialled: the difference is thrown away and never refused.
const WRITTEN = /[^0-9+*#,;()\-.\s/]/;
